import React from 'react';
import { motion } from 'framer-motion';
import { GiMeditation } from 'react-icons/gi';
import {
  FaBullseye,
  FaEye,
  FaHandsHelping,
  FaShieldAlt,
  FaUsers,
  FaFileAlt,
  FaAward,
  FaChartLine,
  FaBalanceScale,
  FaHeart,
  FaGlobe,
  FaLightbulb,
  FaQuoteLeft,
  FaCheckCircle,
  FaTree,
  FaLeaf,
  FaHome,
  FaPaw,
  FaSeedling,
  FaChild
} from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

const AboutPage: React.FC = () => {
  const { t } = useLanguage();
  const coreValues = [
    {
      icon: <FaTree />,
      title: t('envConservation'),
      description: t('envConservationDesc')
    },
    {
      icon: <GiMeditation />,
      title: t('selfKnowledge'),
      description: t('selfKnowledgeDesc')
    },
    {
      icon: <FaLeaf />,
      title: t('sustainableAg'),
      description: t('sustainableAgDesc')
    },
    {
      icon: <FaHome />,
      title: t('heritagePreserv'),
      description: t('heritagePreservDesc')
    },
    {
      icon: <FaPaw />,
      title: t('animalWelfare'),
      description: t('animalWelfareDesc')
    },
    {
      icon: <FaSeedling />,
      title: t('ayurvedicMed'),
      description: t('ayurvedicMedDesc')
    },
    {
      icon: <FaChild />,
      title: t('orphanOldAge'),
      description: t('orphanOldAgeDesc')
    }
  ];

  const teamMembers = [
    { name: 'Swami Upmanyuji', role: 'Founder & Spiritual Guide', expertise: 'Spiritual Leadership, Social Welfare, Humanitarian Service' },
    { name: 'Ms. Priya Sharma', role: 'Executive Director', expertise: 'Non-profit Management, Education' },
    { name: 'Mr. Amit Patel', role: 'Program Director', expertise: 'Environmental Conservation' },
    { name: 'Dr. Sunita Reddy', role: 'Medical Director', expertise: 'Healthcare, Natural Disaster Relief' },
    { name: 'Mr. Ravi Verma', role: 'Legal Advisor', expertise: 'Human Rights, Legal Aid' },
    { name: 'Ms. Neha Gupta', role: 'Finance Director', expertise: 'Non-profit Finance, Fundraising' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-green/20 to-primary-blue/20 py-20">
        <div className="container-lg px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">{t('aboutVishwaBandhu')}</h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto px-4 leading-relaxed">
              {t('aboutSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Organization Overview */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6">{t('ourStory')}</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('ourStoryP1')}
                </p>
                <p>
                  {t('ourStoryP2')}
                </p>
                <p>
                  {t('ourStoryP3')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-green/10 to-primary-blue/10 rounded-2xl p-8"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-green mb-2">15+</div>
                  <div className="text-gray-700">{t('yearsOfService')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-blue mb-2">25+</div>
                  <div className="text-gray-700">{t('citiesReached')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-orange mb-2">100+</div>
                  <div className="text-gray-700">{t('projectsCompleted')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-teal-500 mb-2">50K+</div>
                  <div className="text-gray-700">{t('livesImpacted')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Years of Experience */}
      <section className="section-padding bg-white">
        <div className="container-lg px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">{t('yearsOfExperience')}</h2>
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                {t('yearsExpDesc')}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-3 text-lg"><FaCheckCircle /></span>
                  <span className="text-gray-700 text-lg">This mistaken idea of denouncing pleasure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-3 text-lg"><FaCheckCircle /></span>
                  <span className="text-gray-700 text-lg">Master-builder of human happiness</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-3 text-lg"><FaCheckCircle /></span>
                  <span className="text-gray-700 text-lg">Occasionally circumstances occur in toil</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-3 text-lg"><FaCheckCircle /></span>
                  <span className="text-gray-700 text-lg">Undertakes laborious physical exercise</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="text-5xl text-primary-blue mb-6">
                <FaEye />
              </div>
              <h3 className="text-2xl font-bold mb-4">{t('whatWeDo')}</h3>
              <p className="text-gray-700 text-lg">
                {t('whatWeDoDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="text-5xl text-primary-green mb-6">
                <FaBullseye />
              </div>
              <h3 className="text-2xl font-bold mb-4">{t('howItWorks')}</h3>
              <p className="text-gray-700 text-lg">
                {t('howItWorksDesc')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('ourCoreValues')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('coreValuesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6 text-justify hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-4xl text-primary-green mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="section-padding bg-gradient-to-r from-primary-green/10 to-primary-blue/10">
        <div className="container-lg px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="md:w-1/3 text-center">
                <div className="w-full max-w-xs mx-auto mb-6 overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src="/about-image.png"
                    alt="Swami Upmanyuji - Founder & Spiritual Guide"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-primary-green">Swami Upmanyuji</h3>
                <p className="text-primary-orange font-semibold text-lg">{t('founder')}</p>
                <p className="text-gray-600 mt-2">{t('spiritualLeader')}</p>
              </div>

              <div className="md:w-2/3">
                <div className="text-4xl text-gray-300 mb-4">
                  <span><FaQuoteLeft /></span>
                </div>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  {t('founderQuote1')}
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {t('founderQuote2')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Members */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('ourLeadership')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('leadershipSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6 text-center"
              >
                <div className="w-32 h-32 bg-gray-100 rounded-full mx-auto mb-6 overflow-hidden">
                  <div className="w-full h-full bg-gradient-to-br from-primary-green to-primary-blue flex items-center justify-center">
                    <span className="text-white text-5xl"><FaUsers /></span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-primary-green font-semibold mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.expertise}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration & Legal Information */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-8 text-center">{t('regLegalInfo')}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2 text-primary-green"><FaFileAlt /></span> {t('legalStatus')}
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>Registered under Societies Registration Act, 1860</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>PAN: AAAAA1234B</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>12A & 80G Certified for Tax Exemption</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>FCRA Registered for Foreign Contributions</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2 text-primary-green"><FaAward /></span> {t('certifications')}
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>ISO 9001:2015 Certified for Quality Management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>Guidestar India Platinum Transparency Certified</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>NITI Aayog NGO Darpan Registered</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span>Certified Social Enterprise</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t text-center">
              <h3 className="text-xl font-semibold mb-4">{t('annualReports')}</h3>
              <p className="text-gray-600 mb-6">
                {t('annualReportsDesc')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button className="btn-primary">
                  {t('download2025')}
                </button>
                <button className="btn-secondary">
                  {t('download2024')}
                </button>
                <button className="btn-accent">
                  {t('download2023')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;